import * as React from "react";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Tabs, notification } from "antd";
import { PageLayout } from "src/components/PageLayout";
import { LoaderBox } from "src/components/LoaderBox";
import { cvtError, onError } from "src/common/onError";
import { makeUrl } from "src/routes/makeUrl";
import { PageUrl } from "src/routes/PageUrl";
import { breadItem } from "src/common/breadItem";
import { businessServicesDict } from "src/businessServices/allBusinessServices";
import { bsTabKey, entityCardStore as pageStore } from "./EntityCardStore";
import { EntityFormWithAnchor2 } from "./EntityFormWithAnchor";
import { EntityHistory } from "./EntityHistory";
import { EntityConfirmModal } from "./EntityConfirmModal";
import styles from "./EntityCardPage.module.less";
import { AuditBanner } from "./AuditBanner";
import { useEntityNavBlocker } from "./useEntityNavBlocker";
import { EntityCardTitle } from "./EntityCardTitle";

export const EntityCardPage: React.FC = observer(() => {
  const { id } = useParams(); // URL of this page = PageUrl.entityCard
  React.useEffect(() => {
    if (id) pageStore.init(id);
    return () => {
      pageStore.setCurTab();
    };
  }, [id]);
  const { curTab } = pageStore;
  if (!id) return <Alert type="error" message="Не указан id сущности" />;

  type CardTab = {
    label: React.ReactNode;
    render: () => React.ReactNode;
  };
  const tabs: Record<string, CardTab> = {
    main: {
      label: t("Main"),
      render: () => (
        <div className={styles.entityContent}>
          <LoaderBox remoteData={pageStore.info} drawReady={() => <Inner />} />
        </div>
      ),
    },
    history: {
      label: t("History of changes"),
      render: () =>
        pageStore.historyStore && (
          <div className={styles.entityContent}>
            <EntityHistory
              store={pageStore.historyStore}
              auditStore={pageStore.auditStore}
            />
          </div>
        ),
    },
  };
  pageStore.services.forEach((serv) => {
    const bs = businessServicesDict[serv.serviceKey];
    const viewer = bs?.viewer;
    const renderTabLabel = bs?.renderTabLabel;
    const data =
      pageStore.info.status === "ready"
        ? { ...pageStore.info.result, typesMap: pageStore.attrTypesMap }
        : null;
    if (viewer && data) {
      tabs[bsTabKey(serv.id)] = {
        label: renderTabLabel ? renderTabLabel(serv, data) : serv.name,
        render: () => viewer(serv, data),
      };
    }
  });
  const tabItems = Object.entries(tabs).map(([key, { label }]) => ({
    key,
    label,
  }));

  const { curAuditEvent } = pageStore;
  const auditStore = pageStore.historyStore?.auditStore;

  return (
    <PageLayout
      breadcrumb={[
        breadItem(pageStore.objectName, PageUrl.entities, {
          objectId: pageStore.objectId,
        }),
        breadItem(pageStore.entityName),
      ]}
      documentTitle={pageStore.documentTitle}
    >
      {!!curAuditEvent && !!auditStore && (
        <AuditBanner
          event={curAuditEvent}
          onAuditEvent={(e) => pageStore.onAuditSelected(e)}
          disabled={!pageStore.canClearAudit}
          auditStore={auditStore}
        />
      )}
      <div className={styles.pageContent}>
        <Tabs
          size="middle"
          items={tabItems}
          className={styles.tabs}
          activeKey={curTab}
          onChange={(tab) =>
            pageStore.safeAction(() => pageStore.setCurTab(tab))
          }
        />
        <div className={styles.tabContent}>{tabs[curTab]?.render()}</div>
      </div>
      <EntityConfirmModal store={pageStore} />
    </PageLayout>
  );
});

const Inner: React.FC = observer(() => {
  useEntityNavBlocker(pageStore);
  const { rootBlock } = pageStore;
  const navigate = useNavigate();
  if (!rootBlock) return null;
  const doDelete = async () => {
    try {
      await pageStore.doDelete();
      notification.success({ message: "Выполнено удаление объекта" });
      navigate(makeUrl(PageUrl.entities, { objectId: pageStore.objectId }), {
        replace: true,
      });
    } catch (e) {
      onError(cvtError(e));
    }
  };
  return (
    <EntityFormWithAnchor2
      title={<EntityCardTitle />}
      store={pageStore}
      submitText={t("Save")}
      onSubmit={(values) => pageStore.save(values)}
      onSuccess={() => {
        notification.success({ message: "Выполнено сохранение данных" });
      }}
      disabled={!pageStore.canEntityUpdate || pageStore.buzy}
      availableStates={pageStore.availableStates}
      onChangeState={(stateId, form) => pageStore.changeState(stateId, form)}
      onDelete={pageStore.availableDelete ? doDelete : undefined}
    />
  );
});
